/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @file languageSlice.js
 * @description Redux slice for managing language related state and operations
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import createAxiosInstance from "../async/axios";
import { getRequestParams } from "../async/get-fetch-params";

/**
 * @function getInitialState
 * @description Creates the initial state object for the language slice
 * @param {Object} params - Initial state parameters
 * @returns {Object} The initial state object
 */
export const getInitialState = (languages = null) => ({
  error: null,
  languages,
  loading: false,
});

/**
 * @function getLanguages
 * @description Async thunk for get languages operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const getLanguages = createAsyncThunk("languages/loadLanguages", async (data, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());

    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });

    const result = await axios.get(`/player/languages?originId=${originId}&lineId=${lineId}`);

    return {
      languages: result.data,
    };
  } catch (err) {
    const customError = {
      message: err.response?.headers["x-information"] || "Unable to obtain language details", // serializable (err.response.data)
      name: "Languages List Fetch Error",
      status: err.response?.statusText,
    };
    throw customError;
  }
});

/**
 * @constant languageSlice
 * @description Redux slice for language management
 */
const languageSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(getLanguages.pending, (state) => {
        state.languages = null;
        state.error = null;
        state.loading = true;
      })
      .addCase(getLanguages.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })
      .addCase(getLanguages.fulfilled, (state, action) => {
        state.error = null;
        state.languages = action.payload.languages;
        state.loading = false;
      });
  },
  initialState: getInitialState(),
  name: "language",
  reducers: {},
});
const { reducer } = languageSlice;
export default reducer;
